import { Link, useLocation, useNavigate } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { userService } from "../../../services/api/authenService";
import { toast } from "react-toastify";
import { useSidebarLayout } from "../../../contexts/SidebarLayoutContext";
import { notiService } from "../../../services/api/notiService";
import { getHeaderTitleOverride } from "../../../utils/navigationContext";

const Header = () => {
  const { isSidebarOpen, toggleSidebar, hasSidebar } = useSidebarLayout();
  const { token, dispatch, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [title, setTitle] = useState("");
  const [isUserInfoOpen, setIsUserInfoOpen] = useState(false);
  const [isNotiOpen, setIsNotiOpen] = useState(false);

  const [countNoti, setCountNoti] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isLoadingNoti, setIsLoadingNoti] = useState(false);

  const [page, setPage] = useState(1);
  const [hasMoreNoti, setHasMoreNoti] = useState(true);
  const notiListRef = useRef(null);

  const userInfoRef = useRef(null);
  const notiRef = useRef(null);
  const [popupWidth, setPopupWidth] = useState(0);

  const toggleUserInfo = () => {
    setIsUserInfoOpen((p) => !p);
    setIsNotiOpen(false);
  };
  const toggleNoti = () => {
    setIsNotiOpen((p) => !p);
    setIsUserInfoOpen(false);
  };

  const fetchNotiCount = async () => {
    try {
      const res = await notiService.get_notis_count(token);
      setCountNoti(res?.newNotiCount || 0);
    } catch (err) {
      console.error("Fetch noti count error:", err);
    }
  };

  const fetchNotifications = async (pageToLoad = 1) => {
    try {
      setIsLoadingNoti(true);

      const res = await notiService.get_list_notis(token, {
        page: pageToLoad,
        limit: 10,
      });

      const newList = res?.data || [];
      const pagination = res?.pagination;

      setNotifications((prev) =>
        pageToLoad === 1 ? newList : [...prev, ...newList],
      );

      setPage(pagination?.currentPage || pageToLoad);
      setHasMoreNoti(pagination?.currentPage < pagination?.totalPages);
    } catch (err) {
      console.error("Fetch noti list error:", err);
      toast.error("Không thể tải thông báo");
    } finally {
      setIsLoadingNoti(false);
    }
  };

  /* ================= EFFECTS ================= */

  useEffect(() => {
    if (!token) return;

    fetchNotiCount();
    const interval = setInterval(fetchNotiCount, 30000); // 30 giây
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    if (isNotiOpen) {
      setPage(1);
      setHasMoreNoti(true);
      fetchNotifications(1);
      setCountNoti(0);
    }
  }, [isNotiOpen]);

  useEffect(() => {
    if (userInfoRef.current) {
      setPopupWidth(userInfoRef.current.offsetWidth);
    }
  }, [isUserInfoOpen]);

  useEffect(() => {
    const titleOverride = getHeaderTitleOverride(location);

    if (titleOverride) {
      setTitle(titleOverride);
      return;
    }

    const staticPaths = {
      "/": "Danh sách hồ sơ",
      "/quan-ly-khach-hang": "Quản lý khách hàng",
      "/quan-ly-dich-vu": "Quản lý dịch vụ",
      "/loai-dich-vu": "Loại dịch vụ",
      "/lich-su-thanh-toan": "Lịch sử thanh toán",
      "/lich-su-chinh-sua": "Lịch sử chỉnh sửa",
      "/ho-tro": "Hỗ trợ",
      "/quan-ly-thu-tuc": "Quản lý thủ tục",
      "/tra-cuu": "Tra cứu",
      "/mua-ban-so-huu-tri-tue": "Mua bán sở hữu trí tuệ",
      "/tai-khoan-cua-toi": "Tài khoản của tôi",
      "/danh-sach-tai-khoan": "Danh sách tài khoản",
      "/quan-ly-ho-so": "Danh sách hồ sơ",
      "/danh-sach-hop-dong": "Danh sách hợp đồng",
      "/quan-ly-xet-duyet": "Hồ sơ đề xuất duyệt",
      "/danh-muc-tai-san-tri-tue": "Danh mục tài sản trí tuệ",
    };

    const dynamicPaths = [
      { regex: /\/loai-dich-vu\/[^/]+$/, title: "Chi tiết loại dịch vụ" },
      {
        regex: /\/loai-dich-vu\/[^/]+\/chinh-sua$/,
        title: "Chỉnh sửa loại dịch vụ",
      },
      {
        regex: /\/quan-ly-khach-hang\/khach-hang\/[^/]+$/,
        title: "Chi tiết khách hàng",
      },
      {
        regex: /\/quan-ly-dich-vu\/dich-vu\/[^/]+$/,
        title: "Chi tiết dịch vụ",
      },
      { regex: /\/tai-khoan-cua-toi\/chinh-sua/, title: "Chỉnh sửa tài khoản" },
    ];

    if (staticPaths[location.pathname]) {
      setTitle(staticPaths[location.pathname]);
      return;
    }

    const match = dynamicPaths.find((p) => p.regex.test(location.pathname));
    setTitle(match?.title || "Dịch vụ");
  }, [location]);

  /* ================= LOGOUT ================= */

  const handleLogout = async () => {
    try {
      await userService.log_out();
      dispatch({ type: "LOGOUT" });
      toast.success("Đăng xuất thành công!");
      navigate("/login");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Không thể kết nối tới server.",
      );
    }
  };

  const handleNotiScroll = () => {
    if (isLoadingNoti || !hasMoreNoti) return;

    const el = notiListRef.current;
    if (!el) return;

    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;

    if (nearBottom) {
      fetchNotifications(page + 1);
    }
  };

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Vừa xong";
    if (mins < 60) return `${mins} phút trước`;
    if (mins < 1440) return `${Math.floor(mins / 60)} giờ trước`;
    return d.toLocaleDateString("vi-VN");
  };

  const handleClickNotification = async (notiId) => {
    try {
      const res = await notiService.get_noti_by_id(notiId, token);

      const profileId = res?.data?.profileId?._id;

      // Đóng popup noti
      setIsNotiOpen(false);

      if (profileId) {
        navigate(`/quan-ly-dich-vu-khach-hang/ho-so/${profileId}`);
      } else {
        toast.warning("Không tìm thấy hồ sơ liên quan");
      }
    } catch (error) {
      console.error("Click noti error:", error);
      toast.error("Không thể mở chi tiết thông báo");
    }
  };

  const userDisplayName = user?.fullName || user?.username || "Tài khoản";
  const userRoleLabel = user?.role || "CMS";

  /* ================= RENDER ================= */

  return (
    <header className="relative z-30 flex w-full flex-col border-b border-white/10 bg-[#0155F8] shadow-[0_10px_28px_rgba(1,85,248,0.22)]">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-10 -top-8 h-20 w-20 rounded-full bg-white/10" />
        <div className="absolute left-[44%] top-4 size-5 rounded-full border-[5px] border-white/10" />
        <div className="absolute right-[18%] -top-24 size-48 rounded-full border-[32px] border-white/10" />
        <div className="absolute -bottom-14 left-[72%] h-28 w-28 rounded-full border-[24px] border-white/10" />
      </div>

      <div className="relative z-10 flex min-h-[64px] w-full items-center justify-between gap-4 px-6 py-2">
        <div className="flex min-w-0 items-center gap-3">
          <Link
            to="/"
            className="group inline-flex h-11 shrink-0 items-center gap-2 rounded-xl px-2 text-white transition-colors hover:bg-white/10"
          >
            <img
              src="/logos/logo_vector.svg"
              alt="logo"
              className="size-8 shrink-0 brightness-0 invert"
            />
            <span className="text-xl font-sf-semibold text-white transition-colors group-hover:text-white/90">
              Sohuutritue.vn
            </span>
          </Link>

          {hasSidebar && (
            <button
              type="button"
              onClick={toggleSidebar}
              className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl border border-transparent text-white transition-colors hover:border-white/20 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
              aria-label={isSidebarOpen ? "Thu gọn sidebar" : "Mở rộng sidebar"}
              title={isSidebarOpen ? "Thu gọn" : "Mở rộng"}
            >
              <img
                alt=""
                src="/icons/header-sidebar-toggle.svg"
                className={`size-3.5 brightness-0 invert transition-transform duration-300 ${
                  isSidebarOpen ? "rotate-0" : "rotate-180"
                }`}
              />
            </button>
          )}

          <div className="h-7 w-px shrink-0 bg-white/25" />

          <h1 className="block max-w-[380px] truncate rounded-full px-3 py-2 text-sm font-sf-medium leading-none text-white shadow-sm shadow-blue-950/10 backdrop-blur">
            {title}
          </h1>
        </div>

        <div className="flex shrink-0 items-center gap-2 lg:gap-3">
          <div className="hidden items-center gap-2 text-sm text-white xl:flex">
            <a
              href="tel:0819009697"
              className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-xl border border-white/20 bg-white/10 px-3 shadow-sm shadow-blue-950/10 underline-offset-4 transition-colors hover:border-white/35 hover:bg-white/15 hover:underline"
            >
              <img
                src="/icons/phone-blue.svg"
                alt=""
                className="h-3.5 w-3.5 brightness-0 invert"
              />
              <span>08.1900.9697</span>
            </a>
            <a
              href="mailto:dangky@sohuutritue.vn"
              className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-xl border border-white/20 bg-white/10 px-3 shadow-sm shadow-blue-950/10 underline-offset-4 transition-colors hover:border-white/35 hover:bg-white/15 hover:underline"
            >
              <img
                src="/icons/mail-blue.svg"
                alt=""
                className="h-3.5 w-3.5 brightness-0 invert"
              />
              <span>dangky@sohuutritue.vn</span>
            </a>
          </div>

          <div className="relative" ref={notiRef}>
            <button
              type="button"
              className="relative inline-flex size-10 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white shadow-sm shadow-blue-950/10 transition-colors hover:border-white/35 hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
              onClick={toggleNoti}
              aria-label="Thông báo"
              aria-haspopup="dialog"
              aria-expanded={isNotiOpen}
            >
              <img
                src="/icons/notification.svg"
                className="size-3.5 shrink-0 brightness-0 invert"
                alt=""
              />
              {countNoti > 0 && (
                <span className="absolute -right-1.5 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border border-white bg-red-600 px-1 text-[10px] text-white">
                  {countNoti > 99 ? "99+" : countNoti}
                </span>
              )}
            </button>

            {isNotiOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={toggleNoti} />

                <div className="absolute right-0 top-[calc(100%+8px)] z-[60] flex max-h-[500px] w-96 flex-col overflow-hidden rounded-xl border border-slate-100 bg-white shadow-[0_16px_40px_rgba(15,23,42,0.14)]">
                  <div className="border-b p-4 font-sf-semibold">Thông báo</div>

                  <div
                    ref={notiListRef}
                    onScroll={handleNotiScroll}
                    className="flex-1 overflow-y-auto"
                  >
                    {notifications.length === 0 && !isLoadingNoti ? (
                      <p className="p-5 text-center text-sm text-slate-500">
                        Không có thông báo
                      </p>
                    ) : (
                      notifications.map((n) => (
                        <div
                          key={n._id}
                          onClick={() => handleClickNotification(n._id)}
                          className={`flex cursor-pointer items-start gap-4 border-b border-slate-100 p-4 transition-colors hover:bg-blue-50 ${
                            n.status !== "Read" ? "bg-blue-50" : ""
                          }`}
                        >
                          <div className="h-8 w-8 shrink-0">
                            <img
                              alt=""
                              className="h-8 w-8 shrink-0"
                              src="/images/notification-icon.png"
                            />
                          </div>
                          <div>
                            <p className="text-sm">{n.message}</p>
                            <p className="mt-1 text-xs text-gray-500">
                              {formatDate(n.createdAt)}
                            </p>
                          </div>
                        </div>
                      ))
                    )}

                    {isLoadingNoti && (
                      <div className="p-3 text-center text-sm text-gray-500">
                        Đang tải thêm...
                      </div>
                    )}

                    {!hasMoreNoti && notifications.length > 0 && (
                      <div className="p-2 text-center text-xs text-gray-400">
                        Đã tải hết thông báo
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <button
            type="button"
            ref={userInfoRef}
            onClick={toggleUserInfo}
            className="group flex h-12 w-auto max-w-[320px] cursor-pointer items-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-2.5 py-1.5 text-white shadow-sm shadow-blue-950/10 backdrop-blur transition-colors hover:border-white/35 hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
            aria-haspopup="menu"
            aria-expanded={isUserInfoOpen}
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="min-w-0 text-left">
                <p
                  className="max-w-[180px] truncate text-base font-sf-semibold leading-5 text-white"
                  title={userDisplayName}
                >
                  {userDisplayName}
                </p>
                <p className="text-xs font-be-vietnam-light leading-4 text-white/75">
                  {userRoleLabel}
                </p>
              </div>
              <img
                src={
                  user?.avatar ||
                  user?.avatarUrl ||
                  "/images/avatar-default.png"
                }
                alt="avatar"
                className="size-8 shrink-0 rounded-full object-cover object-center ring-2 ring-white/30"
              />
            </div>
            <img
              src="/icons/arrow-down.svg"
              alt=""
              className={`size-5 brightness-0 invert opacity-80 transition-transform duration-300 group-hover:opacity-100 ${
                isUserInfoOpen ? "rotate-180" : "rotate-0"
              }`}
            />
          </button>
        </div>
      </div>

      {isUserInfoOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={toggleUserInfo} />
          <div
            className="absolute right-4 top-[calc(100%+8px)] z-[60] overflow-hidden rounded-xl border border-slate-100 bg-white shadow-[0_16px_40px_rgba(15,23,42,0.14)]"
            style={{ width: popupWidth || "auto" }}
          >
            <Link
              to="/tai-khoan-cua-toi"
              className="block px-4 py-3 text-sm font-sf-medium text-blue-600 transition-colors hover:bg-blue-50"
              onClick={toggleUserInfo}
            >
              Xem thông tin tài khoản
            </Link>
            <button
              onClick={() => {
                toggleUserInfo();
                handleLogout();
              }}
              className="block w-full px-4 py-3 text-left text-sm font-sf-medium text-red-600 transition-colors hover:bg-red-50"
            >
              Đăng xuất
            </button>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
